import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, BadgeCheck, CheckCircle2, MapPin } from "lucide-react";
import { statusDotColor } from "@/lib/businessStatus";
import PinButton from "@/components/PinButton";

const ROTATIONS = ["-rotate-2", "-rotate-1", "rotate-1", "rotate-2"];

// Homepage-only "Pros of the Week" card. Larger photo + tinted cover header +
// more breathing room than the compact BusinessCard used on Search/Browse.
export default function HomeBusinessCard({ pro, index = 0, workedTogether = false }) {
  const navigate = useNavigate();
  const [lifting, setLifting] = useState(false);
  const rotation = ROTATIONS[index % ROTATIONS.length];
  const cardBg = pro.card_color || "#FFFFFF";
  const pinColor = pro.pin_color || "#EF4444";
  const isExample = !!(pro.is_example || pro.sample_content);
  const isFeatured = pro.is_pro_plus;

  const badges = [];
  if (workedTogether) badges.push({ key: "worked", Icon: CheckCircle2, label: "Worked Together", cls: "bg-[#2E6B57] text-[#FFFFFF]" });
  if (pro.verified) badges.push({ key: "verified", Icon: BadgeCheck, label: "Verified Profile", cls: "bg-[#2E6B57]/15 text-[#2C6653]", title: "LIMYA automatically verified this Pro's email and profile completeness. This is not a license verification or endorsement." });
  const visibleBadges = badges.slice(0, 2);

  const handleClick = (e) => {
    e.preventDefault();
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) { navigate(`/pro/${pro.user_id}`); return; }
    setLifting(true);
    setTimeout(() => navigate(`/pro/${pro.user_id}`), 230);
  };

  return (
    <a
      href={`/pro/${pro.user_id}`}
      onClick={handleClick}
      data-testid={`home-business-card-${pro.user_id}`}
      className={`group relative block paper-card ${rotation} ${lifting ? "lift-card" : ""} rounded-2xl overflow-visible w-full`}
      style={{ background: cardBg }}
    >
      <div className="push-pin" style={{ "--pin": pinColor }} />

      {isExample && (
        <div className="absolute top-3 right-3 z-10 bg-[#FFFDF6] text-[#5C554E] border border-[#5C554E]/35 rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide" data-testid={`example-badge-${pro.user_id}`}>
          EXAMPLE
        </div>
      )}

      {isFeatured && !isExample && (
        <div className="absolute top-3 right-3 z-10 bg-[#222321] text-[#D6B977] border border-[#D6B977] rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide" data-testid={`pro-plus-ribbon-${pro.user_id}`}>
          PRO+
        </div>
      )}

      {/* Tinted cover header, keyed to the pro's pin color */}
      <div
        className="h-20 rounded-t-2xl paper-tex"
        style={{ background: `linear-gradient(135deg, ${pinColor}33 0%, ${pinColor}14 60%, transparent 100%)` }}
      />

      <div className="px-6 pb-6 -mt-11 paper-tex rounded-b-2xl">
        {/* Larger avatar overlapping the cover */}
        <div className="relative w-[88px] h-[88px] rounded-full overflow-hidden ring-4 ring-white shadow-lg bg-[#F2F4F2] mb-3">
          {pro.avatar_url ? (
            <img src={pro.avatar_url} alt={pro.business_name} className="w-full h-full object-cover" loading="lazy"/>
          ) : (
            <div className="w-full h-full grid place-items-center bg-[#2E6B57] text-[#FFFFFF] font-bold text-3xl">
              {pro.business_name?.[0] || "?"}
            </div>
          )}
          {statusDotColor(pro.business_status) && (
            <span
              className="absolute bottom-1 right-1 w-4 h-4 rounded-full ring-2 ring-white"
              style={{ background: statusDotColor(pro.business_status) }}
              title="Business status"
              data-testid={`business-status-dot-${pro.user_id}`}
            />
          )}
        </div>

        <h3 className="font-bold text-[#222] text-lg leading-tight line-clamp-2" style={{fontFamily:'Outfit'}}>{pro.business_name}</h3>
        <p className="text-xs text-[#222]/60 uppercase tracking-wider font-medium mt-1">{pro.category}</p>
        {pro.city && (
          <p className="text-xs text-[#222]/50 mt-1 flex items-center gap-1"><MapPin size={12} className="shrink-0"/> {pro.city}</p>
        )}

        {pro.bio && (
          <p className="text-sm text-[#222]/65 leading-relaxed mt-3 line-clamp-2" data-testid={`card-tagline-${pro.user_id}`}>{pro.bio}</p>
        )}

        <div className="flex items-center gap-1.5 text-sm mt-4">
          <Star size={15} className="fill-[#E0A83E] text-[#E0A83E]"/>
          <span className="font-bold">{pro.rating?.toFixed(1) || "—"}</span>
          <span className="text-[#222]/50 text-xs">({pro.review_count || 0})</span>
        </div>

        {visibleBadges.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {visibleBadges.map(b => (
              <span key={b.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${b.cls}`} data-testid={`badge-${b.key}-${pro.user_id}`} title={b.title || ""}>
                <b.Icon size={10}/> {b.label}
              </span>
            ))}
          </div>
        )}

        <div onClick={(e) => e.stopPropagation()} className="pt-4">
          <PinButton pro={pro} size="sm" label="Pin" pinnedLabel="Pinned"/>
        </div>
      </div>
    </a>
  );
}
